import { CountryInfoWithCurrency } from '@shared/types/country-info';
import { OfferType } from '@shared/types/offer-type';
import { BaseOffer, CurrencyType } from '@shared/types/offers';
import clsx from 'clsx';
import { FC, useMemo } from 'react';
import { general, icons } from '../../constants/assets';
import { useTranslation } from '../../i18n/useTranslation';
import { CountdownTimer } from '../CountdownTimer';

type OfferCardProps = {
  type: OfferType;
  offerName: string
  bonus?: string
  description?: string
  expiredAt: Date | undefined
  imageUrl: string
  backgroundUrl: string
  buttonType: string
  isLimitReached: boolean
  isSoldOut: boolean
  price: number
  usdcPrice: number
  proceedToCheckout: VoidFunction
  items: BaseOffer['sku']['items']
  refreshTime?: Date
  displayOfferType: BaseOffer['sku']['displayOfferType']
  currency: CurrencyType
  countryInfo: CountryInfoWithCurrency,
}

export const OfferCard: FC<OfferCardProps> = ({
  type,
  offerName,
  bonus,
  expiredAt,
  imageUrl,
  backgroundUrl,
  buttonType,
  price,
  usdcPrice,
  description,
  isLimitReached,
  isSoldOut,
  proceedToCheckout,
  refreshTime,
  items,
  displayOfferType,
  currency,
  countryInfo,
}) => {
  const { offerCardTranslations } = useTranslation();
  const isUnavailable = isLimitReached || isSoldOut;

  const itemQuantitiy = useMemo(() => {
    const firstKey = Object.keys(items)[0];

    if (!firstKey) {
      return undefined;
    }

    return items[firstKey as keyof BaseOffer['sku']['items']];
  }, [items]);

  const Button = useMemo(() => {
    const buttonClassName = `${buttonType}-button`;

    if (isUnavailable) {
      return (
        <button
          type="button"
          disabled
          className={clsx(buttonClassName, 'font-border poppins-lg h-[69px] w-[209px]')}
        >
          {isSoldOut
            ? offerCardTranslations.soldOut
            : offerCardTranslations.limitReached}
        </button>
      );
    }

    return (
      <button
        type="button"
        className={clsx(
          buttonClassName,
          'flex h-[69px] w-[209px] flex-col hover:brightness-125',
        )}
        onClick={proceedToCheckout}
      >
        {currency === 'FIAT' ? (
          <div className="font-border poppins-2xl flex h-[34px] items-center">
            <span>
              {countryInfo.currencySymbol}
              {' '}
              {price.toLocaleString()}
              {' '}
            </span>
          </div>
        ) : (
          <>
            <div className="font-border poppins-2xl flex h-[34px] items-center">
              <span>{price.toLocaleString()}</span>
              <img
                className="size-[21px] md:size-[34px]"
                src={icons.powerIcon}
                alt="power-icon"
              />
            </div>
            {usdcPrice && (
              <span className="poppins-sm text-[#005737]">
                {usdcPrice.toLocaleString()}
                {' '}
                {offerCardTranslations.usdc}
              </span>
            )}
          </>
        )}

      </button>
    );
  }, [proceedToCheckout, price, usdcPrice, isSoldOut, isUnavailable, currency]);

  return (
    <div
      className={clsx(
        'relative flex h-full flex-col rounded-lg border-[1.5px] border-b-4 border-solid border-black transition-transform duration-300 ease-in-out hover:scale-105',
        isUnavailable && 'brightness-75 grayscale-[0.6]',
      )}
      style={{
        backgroundImage: `url(${backgroundUrl})`,
        backgroundSize: 'cover',
      }}
    >
      {bonus && (
        <div className="font-border poppins-md absolute left-[-14px] top-[-10px] z-10 flex h-[40.5px] w-[131.41px] items-center bg-[url(/assets/bonus-flag.png)] bg-cover bg-no-repeat pl-4 md:-top-4 md:left-[-18px] md:h-[50px] md:w-[164.5px] md:pl-6">
          {bonus}
        </div>
      )}
      {expiredAt && (
        <div className="absolute inset-x-0 top-0 flex items-center justify-end rounded-t-md bg-[#0E122C66] px-3 py-1 [clip-path:polygon(0_0,100%_0%,100%_100%,0%_80%)]">
          <span className="poppins-sm font-border uppercase">
            {offerCardTranslations.expiresIn}
            {' '}
            <CountdownTimer targetDate={expiredAt} />
          </span>
        </div>
      )}
      <div className="relative mt-6 flex min-h-[200px] flex-1 items-center justify-center overflow-hidden">
        <div className="absolute inset-x-3 top-3 flex flex-row-reverse">
          {itemQuantitiy && (
            <div className="font-border poppins-lg rounded-[10px] bg-[#0E122C33] px-[14px] py-[5px]">
              {itemQuantitiy.toLocaleString()}
            </div>
          )}
        </div>
      </div>
      <div className="relative z-0">
        <div className="absolute top-[-169px] flex h-[213px] w-full overflow-hidden">
          {displayOfferType !== 'grand' ? (
            <img
              className={clsx(
                'absolute left-1/2 top-[-15px] size-[230px] -translate-x-1/2',
                isUnavailable && 'opacity-60',
              )}
              src={imageUrl}
              alt="offer-item"
            />
          ) : (
            <img
              className={clsx(
                'absolute left-1/2 top-[-15px] h-[230px] w-[303px] max-w-none -translate-x-1/2',
                isUnavailable && 'opacity-60',
              )}
              src={imageUrl}
              alt="offer-item"
            />
          )}
        </div>
        <div
          className="
            absolute
            inset-x-[-2px]
            bottom-[-4px]
            top-0
            z-0
            rounded-b-lg
            bg-black
            [clip-path:polygon(0_0,100%_20%,100%_100%,0%_100%)]
          "
        >
          <div
            className="
              absolute inset-x-[1.5px] bottom-[6px] top-[1.5px] rounded-b-lg
              [background:linear-gradient(179.98deg,#B149EC_21.24%,#541CAC_155.27%)]
              [box-shadow:0px_-2.44px_0px_0px_#330875_inset]
              [clip-path:polygon(0_0,100%_21%,100%_100%,0%_100%)]
            "
          />
        </div>
        <div className="relative z-10 flex flex-col items-center">
          <span className="font-border poppins-lg flex min-h-[50px] max-w-[200px] items-center justify-center text-center md:min-h-[70px] md:max-w-[250px]">
            {offerName}
          </span>
          <div className="flex flex-col items-center gap-2 px-[10px] pb-4 pt-2 md:px-9">
            {Button}
            <span
              className={clsx(
                'font-objektiv-mk3 flex h-[17.84px] items-center gap-1 text-[10px] font-black 2xl:text-[12.5px]',
                isUnavailable ? 'text-deactivatedWhite' : 'text-[#CDB3F1]',
              )}
            >
              {isSoldOut && type === 'regular' ? '' : description}
              {isLimitReached && refreshTime && (
                <div className="objektiv-mk3-sm flex items-center rounded-[2px] bg-black p-[2px] pr-1">
                  <img
                    src={general.pictoiconHourglass}
                    alt="hourglass"
                    className="size-[15px]"
                  />
                  <span className="h-[16px] lowercase">
                    <CountdownTimer targetDate={refreshTime} />
                  </span>
                </div>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
